import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { services as fallbackServices, contactInfo as fallbackContactInfo } from '../data';
import { useCms } from '../context/CmsContext';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { services, settings, getPage } = useCms();

  // Find static fallback service
  const staticService = fallbackServices.find((s) => s.slug === slug);

  // Find CMS service matching the slug or title
  const cmsService = (services || []).find((s) => {
    const sSlug = typeof s.slug === 'object' ? s.slug?.da : s.slug;
    const sTitle = typeof s.title === 'object' ? s.title?.da : s.title;
    if (sSlug === slug) return true;
    if (slug === 'fredede-bygninger' && (sSlug?.includes('fredede') || sTitle?.toLowerCase().includes('fredede'))) return true;
    if (slug === 'facaderens-murvaerk' && (sSlug?.includes('facade') || sTitle?.toLowerCase().includes('facade'))) return true;
    if (slug === 'graffiti-beton-natursten' && (sSlug?.includes('graffiti') || sTitle?.toLowerCase().includes('graffiti'))) return true;
    if (slug === 'rust-metaloverflader' && (sSlug?.includes('rust') || sTitle?.toLowerCase().includes('rust'))) return true;
    if (slug === 'industri-skadeservice' && (sSlug?.includes('industri') || sTitle?.toLowerCase().includes('industri'))) return true;
    return false;
  });

  const currentService = cmsService || staticService;

  useEffect(() => {
    if (!staticService && !cmsService) {
      navigate('/404', { replace: true });
      return;
    }

    window.scrollTo(0, 0);
  }, [slug, navigate, staticService, cmsService]);

  if (!currentService && !staticService) return null;

  // Title
  const title = (slug === 'fredede-bygninger'
    ? 'Laserrensning af fredede og bevaringsværdige bygninger'
    : slug === 'facaderens-murvaerk'
    ? 'Facaderens med laser til murværk og byggeri'
    : slug === 'industri-skadeservice'
    ? 'Laserrens til industri og skadeservice med præcis kontrol'
    : ((typeof currentService?.title === 'object' ? currentService?.title?.da : currentService?.title) || staticService?.title || ''));
  
  // Short description
  const shortDesc = (typeof currentService?.short_desc === 'object' ? currentService?.short_desc?.da : currentService?.short_desc) || staticService?.short_desc || '';
  
  // Image
  const defaultServiceImage = slug === 'facaderens-murvaerk'
    ? 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/img-20260514-075654.jpg'
    : 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png';

  const rawImage = Array.isArray(currentService?.image) && currentService.image.length > 0
    ? currentService.image[0]
    : typeof currentService?.image === 'string'
    ? currentService.image
    : staticService?.image || defaultServiceImage;

  const heroImage = slug === 'facaderens-murvaerk'
    ? '/cdn-cgi/image/width=1800,quality=80,format=auto/_assets/img-3435.jpg'
    : slug === 'industri-skadeservice'
    ? '/cdn-cgi/image/width=1800,quality=80,format=auto/_assets/rampe-med-maling-1-1024x923.jpg'
    : rawImage.includes('assets.auxodev.dk') && !rawImage.includes('/cdn-cgi/image/')
    ? rawImage.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1800,quality=80,format=auto/assets/')
    : rawImage;

  const cardImage = slug === 'facaderens-murvaerk'
    ? '/cdn-cgi/image/width=800,quality=85,format=auto/_assets/gilleleje-3-1024x461.jpg'
    : slug === 'industri-skadeservice'
    ? '/cdn-cgi/image/width=800,quality=85,format=auto/_assets/metalrampe-650x650.jpg'
    : rawImage.includes('assets.auxodev.dk') && !rawImage.includes('/cdn-cgi/image/')
    ? rawImage.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/')
    : rawImage;

  // Advantages parsing
  const rawAdv = typeof currentService?.advantages === 'object' ? currentService?.advantages?.da : currentService?.advantages;
  const advantagesList = Array.isArray(rawAdv)
    ? rawAdv
    : typeof rawAdv === 'string'
    ? rawAdv.split('\n').map((s) => s.trim()).filter(Boolean)
    : staticService?.advantages || [];

  // Process parsing
  const rawProc = typeof currentService?.process === 'object' ? currentService?.process?.da : currentService?.process;
  const processList = Array.isArray(rawProc)
    ? rawProc
    : typeof rawProc === 'string'
    ? rawProc.split('\n').map((s) => s.trim()).filter(Boolean)
    : staticService?.process || [];

  // Content HTML
  const pageEntry = getPage ? getPage(slug) : null;
  const contentHtml = (typeof currentService?.content_html === 'object' ? currentService?.content_html?.da : currentService?.content_html)
    || (pageEntry?.content_html?.da)
    || '';

  const phone = settings?.phone || fallbackContactInfo.phone;

  // SEO & Meta
  const seoTitle = slug === 'fredede-bygninger'
    ? 'Laserrensning af fredede bygninger i hele Danmark | Kontakt her'
    : slug === 'facaderens-murvaerk'
    ? 'Facaderens med laser til murværk i hele Danmark | Få tilbud'
    : slug === 'industri-skadeservice'
    ? 'Laserrens til industri og skadeservice i hele Danmark | Få tilbud'
    : `${title} - Ecolaser ApS`;
  const seoDesc = slug === 'fredede-bygninger'
    ? 'Laserrensning af fredede og bevaringsværdige bygninger i hele Danmark. Vi renser natursten, murværk, stuk og ornamenter. Få en vurdering i dag.'
    : slug === 'facaderens-murvaerk'
    ? 'Få facaderens med laser til mursten og facader i hele Danmark. Vi fjerner kalkslør, salpeterudtræk, graffiti og smuds. Kontakt os for et tilbud.'
    : slug === 'industri-skadeservice'
    ? 'Mobil og kemifri laserrens til industri, skadeservice, procesudstyr og sodsanering i hele Danmark. Ingen vand eller kemikalier. Kontakt os for tilbud.'
    : shortDesc;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={rawImage} />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white">{title}</h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Left Column: Content + Advantages & Process */}
            <div className="lg:col-span-7 space-y-12">
              {slug === 'facaderens-murvaerk' ? (
                <div className="space-y-10 text-zinc-700 leading-relaxed text-base md:text-lg">
                  {/* SEO Intro Text */}
                  <div className="space-y-4">
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Ecolaser udfører specialiseret{' '}
                      <Link
                        to="/"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        facaderens med laser og laserafrensning af nybyggeri og ældre ejendomme
                      </Link>
                      . Vi hjælper blandt andet bygherrer, entreprenører, rådgivere, virksomheder og offentlige aktører med kontrolleret afrensning af facader, mursten, tegl, beton og andre mineralske overflader.
                    </p>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Inden for professionel facaderens er ingen mursten ens; alder, brænding og erosionsgrad gør hvert murværk unikt. Med kontrolleret laserteknologi tilpasses behandlingen derfor den konkrete facade, murstenens overflade og den belægning, der skal fjernes.
                    </p>
                  </div>

                  {/* Intro Image Showcase */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/img-3498.jpg"
                        alt="Håndværker med beskyttelsesbriller udfører kontrolleret laserrensning på en murstensfacade"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Kontrolleret og skånsom facaderens med mobil laser – 100% tør proces uden vand, kemi eller slid</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Primary CTA Buttons */}
                  <div className="pt-2 flex flex-wrap items-center gap-4">
                    <a
                      href="tel:29100810"
                      className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                      <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                      <span>Send en mail</span>
                    </a>
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-2.5 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-200"
                    >
                      <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
                      <span>Få et tilbud</span>
                    </Link>
                  </div>

                  {/* Section 1 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Målrettet fjernelse af salpeterudtræk og kalkslør
                    </h2>
                    <p>
                      Ecolaser udfører målrettet fjernelse af salpeterudtræk, kalkaflejringer og hvidt slør på blandt andet nylagt murværk. Her kan laserrens af mursten være relevant, når der stilles krav til en kontrolleret behandling af overfladen uden mekanisk slibning.
                    </p>
                    <p>
                      Metoden kan også anvendes til afrensning af graffiti, sod, biologisk vækst og atmosfærisk smuds. Ved laserafrensning af mursten tilpasses laserens parametre til materialets overflade, sårbarhed og tilstand.
                    </p>
                    <p>
                      Når en facade renses med laser, arbejdes der målrettet på den belægning, der skal fjernes. Det gør metoden velegnet til både nybyggeri og eksisterende ejendomme, hvor den færdige facade skal behandles med fokus på materialets oprindelige udtryk.
                    </p>
                  </div>

                  {/* Section 2 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Skånsom og fuldstændig tør bearbejdning
                    </h2>
                    <p>
                      Metodens store fordel ved facadeafrensning er den skånsomme, fuldstændig tørre bearbejdning. Da der ikke tilføres vand til murstenens kapillarer under selve afrensningen, begrænses fugtpåvirkningen af murværket.
                    </p>
                    <p>
                      Processen er samtidig fri for rensekemi, syre og blæsemidler. Det gør laserafrensning af facade og murværk relevant til alt fra hårdfør granit til følsomme tegl-, murstens- og betonoverflader, hvor traditionelle rensemetoder kan medføre unødig påvirkning eller materialenedbrydning.
                    </p>
                    <p>
                      Ved nybyggeri kan den tørre behandling være særligt relevant, når bygherre eller entreprenør ønsker at få udbedret synlige overfladeproblemer uden at tilføre ekstra fugt til den færdige konstruktion.
                    </p>
                  </div>

                  {/* Mid-content Image Showcase */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/img-3435.jpg"
                        alt="Ecolaser håndværker udfører facaderensning fra stige"
                        className="w-full h-full object-cover object-[center_35%]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Skånsom og mobil laserafrensning af facader – her i aktion direkte ved bygningen</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Beskyttelse af murstenens vitale brandhud
                    </h2>
                    <p>
                      Lysstrålen nedbryder sekundære belægninger, graffitirester og trafikfilm uden mekanisk slid, hvilket er med til at beskytte både fuger, murværk og sten.
                    </p>
                    <p>
                      Gennem en nøjagtig kalibrering af laserens parametre tilpasses behandlingen den konkrete mursten og dens overflade. Der er særligt fokus på at bevare murstenens vitale brandhud og oprindelige struktur under afrensningen.
                    </p>
                    <p>
                      På følsomme eller mere komplekse facader kan en{' '}
                      <Link
                        to="/priser"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        prøverensning
                      </Link>{' '}
                      være relevant. Her vurderes murværkets reaktion på behandlingen, før den endelige metode fastlægges. Det giver mulighed for at tilpasse intensitet og arbejdsgang til den konkrete opgave og samtidig tage hensyn til murværkets visuelle og strukturelle integritet.
                    </p>
                  </div>

                  {/* Mid-content CTA Banner */}
                  <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-6 md:p-8 space-y-4 shadow-sm">
                    <div>
                      <span className="text-xs font-bold text-brand-green uppercase tracking-wider block mb-1">
                        Prøverensning &amp; rådgivning
                      </span>
                      <h3 className="text-lg md:text-xl font-bold text-zinc-950">
                        Få en faglig vurdering af dit murværk
                      </h3>
                      <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                        Vi udfører gerne et uforpligtende prøvefelt direkte på byggepladsen eller facaden, så du kan se det skånsomme resultat før opstart.
                      </p>
                    </div>
                    <div className="pt-2 flex flex-wrap items-center gap-3">
                      <a
                        href="tel:29100810"
                        className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-5 py-3 rounded-xl text-sm transition-all duration-300 shadow-sm"
                      >
                        <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                        <span className="text-white">Ring 29 10 08 10</span>
                      </a>
                      <a
                        href="mailto:info@ecolaser.dk"
                        className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-5 py-3 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                      >
                        <i className="fa-solid fa-envelope" aria-hidden="true" />
                        <span>Send en mail</span>
                      </a>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2.5 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-5 py-3 rounded-xl text-sm transition-all duration-300 border border-zinc-200"
                      >
                        <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
                        <span>Indhent tilbud</span>
                      </Link>
                    </div>
                  </div>

                  {/* Section 4 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Laserrens af facade ved nybyggeri og renovering
                    </h2>
                    <p>
                      Laserrens kan indgå både i afslutningen af et nybyggeri og ved renovering af eksisterende ejendomme. For bygherrer, entreprenører og rådgivere betyder det, at afrensningen kan planlægges som en del af det samlede projekt og tilpasses kravene til den færdige facade.
                    </p>
                    <p>
                      Ecolaser arbejder med mobilt udstyr og kan udføre behandlingen direkte på byggepladsen eller ved den eksisterende ejendom. Udstyret kan anvendes fra blandt andet lift og stillads, når adgangsforholdene kræver det.
                    </p>
                    <p>
                      Ved større byggeprojekter kan laserrens af facade og mursten være relevant som en afsluttende behandling, når kalkslør, salpeterudtræk, smuds eller andre belægninger skal fjernes fra udvalgte områder.
                    </p>
                  </div>
                </div>
              ) : slug === 'fredede-bygninger' ? (
                <div className="space-y-10 text-zinc-700 leading-relaxed text-base md:text-lg">
                  {/* SEO Intro Text */}
                  <div className="space-y-4">
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Ecolaser udfører specialiseret, højteknologisk og skånsom{' '}
                      <Link
                        to="/"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        laserrensning af fredede, bevaringsværdige og historiske bygninger
                      </Link>
                      , hvor overfladerne kræver en høj grad af præcision. Vi hjælper blandt andet bygherrer, entreprenører, rådgivere, offentlige aktører og ejendomsejere i forbindelse med restaurering, renovering og vedligeholdelse af historiske ejendomme.
                    </p>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Med udgangspunkt i dybdegående materialeforståelse omfatter arbejdet beskyttelse og vedligeholdelse af vores fælles kulturarv - fra større{' '}
                      <Link
                        to="/ydelser/facaderens-murvaerk"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        facadearbejder
                      </Link>{' '}
                      til nænsom afrensning af friser, stuk og ornamenter udført i f.eks. sandsten eller gips.
                    </p>
                  </div>

                  {/* Intro Image Showcase */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/ornament-650x650.jpg"
                        alt="Nænsom laserrensning af historisk ornament på søjle"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Berøringsfri og kemifri laserrensning af sarte sandstensornamenter og historiske detaljer</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Primary CTA Buttons */}
                  <div className="pt-2 flex flex-wrap items-center gap-4">
                    <a
                      href="tel:29100810"
                      className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                      <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                      <span>Send en mail</span>
                    </a>
                  </div>

                  {/* Section 1 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Skånsom restaurering af vores fælles kulturarv
                    </h2>
                    <p>
                      På historiske facader kan en for hård behandling ændre overfladen permanent. Derfor kræver laserrens af historiske bygninger en konkret vurdering af både materiale, belægning og ønsket renseniveau.
                    </p>
                    <p>
                      Laserafrensning foregår uden mekanisk slibning, procesvand, syre og rensekemikalier. Det gør metoden relevant, hvor historisk patina, profileringer og sarte detaljer skal behandles med respekt for bygningens oprindelige udtryk.
                    </p>
                    <p>
                      Før arbejdet påbegyndes, vurderer vi blandt andet materialets alder, hårdhed, porøsitet, varmefølsomhed og sårbarhed. På mere komplekse overflader kan en{' '}
                      <Link
                        to="/priser"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        prøverensning
                      </Link>{' '}
                      danne grundlag for den videre behandling.
                    </p>
                  </div>

                  {/* Section 2 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Præcis kalibrering til sarte natursten
                    </h2>
                    <p>
                      Metodens kernekompetence er den præcise styring af laserteknologien, hvor lysstrålens intensitet og frekvens programmeres og finjusteres efter det enkelte materiale.
                    </p>
                    <p>
                      Det gør{' '}
                      <Link
                        to="/ydelser/graffiti-beton-natursten"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        laserafrensning af natursten
                      </Link>{' '}
                      relevant til både robuste granitoverflader og mere sarte materialer som kalksten, sandsten og marmor samt historiske gipsarbejder på ældre ejendomme.
                    </p>
                    <p>
                      Laserlyset kan samtidig føres over komplekse former og ned i profileringer, dybe hulkehler og mikrostrukturer. Metoden kan derfor anvendes på blandt andet friser, stuk, ornamenter, figurer og andre detaljer, hvor manuel eller mekanisk afrensning kan være vanskelig at styre præcist.
                    </p>
                  </div>

                  {/* Mid-content Image Showcase */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/granitfacade-chr-d-9-c2-b4s-gade-650x650.jpg"
                        alt="Laserrensning af historiske granitarkader på facade"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Præcis laserafrensning af historiske granitarkader og natursten uden slid eller kemi (Christian IX's Gade, Kbh)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Bevaring af historisk patina og overfladestruktur
                    </h2>
                    <p>
                      Laserrensning kan anvendes til fjernelse af overfladesnavs, sod, trafikfilm, atmosfærisk smuds og sekundære belægninger uden fysisk kontakt med materialet.
                    </p>
                    <p>
                      Målet er ikke nødvendigvis at skabe en kunstigt ny overflade. Ved restaurering af kulturarv fastlægges renseniveauet med fokus på at bevare materialets oprindelige overfladestruktur, historiske patina og visuelle udtryk.
                    </p>
                    <p>
                      På historisk murværk vurderes også murstenenes brænding, erosionsgrad, fuger og brandhud. Det er særligt vigtigt ved ældre tegl, hvor materialets tilstand kan variere betydeligt fra område til område.
                    </p>
                  </div>

                  {/* Section 4 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Faglig ekspertise og materialeforståelse
                    </h2>
                    <p>
                      Fundamentet for arbejdet er den faglige ekspertise til at vurdere, hvor grænsen for det oprindelige materiales naturlige holdbarhed og bæreevne ligger. Det er med til at begrænse risikoen for strukturelle skader og bevare bygningens byggetekniske integritet.
                    </p>
                    <p>
                      På fredede ejendomme kan der samtidig være særlige krav til projektet. Bygherre, rådgiver eller entreprenør bør derfor afklare eventuelle krav og tilladelser med relevante myndigheder, inden arbejdet sættes i gang.
                    </p>
                    <p>
                      Ecolaser kan indgå som specialist i projektet og tilpasse afrensningen til de krav, der er fastlagt for materialer, renseniveau og færdigt udtryk.
                    </p>
                  </div>
                </div>
              ) : slug === 'industri-skadeservice' ? (
                <div className="space-y-10 text-zinc-700 leading-relaxed text-base md:text-lg">
                  {/* SEO Intro Text */}
                  <div className="space-y-4">
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Ecolaser udfører specialiseret og mobil{' '}
                      <Link
                        to="/"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        laserrens til industri og skadeservice
                      </Link>{' '}
                      i hele Danmark. Vi hjælper blandt andet bygherrer, entreprenører, rådgivere, virksomheder og offentlige aktører med afrensning i forbindelse med{' '}
                      <Link
                        to="/ydelser/facaderens-murvaerk"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        nybyggeri
                      </Link>
                      , renovering, vedligeholdelse og skadeservice.
                    </p>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Ved akutte indsatser og industrielle renseopgaver gør laserens præcision det muligt at arbejde målrettet på den konkrete overflade. Traditionelle metoder kan efterlade store mængder sand, vand eller kemikalierester. Vores kontrollerede laserteknologi arbejder derimod uden procesvand, rensekemi og blæsemidler.
                    </p>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Da vi ikke bruger vand under selve afrensningen, er laserrens særligt velegnet til indendørs opgaver, hvor man ønsker at begrænse risikoen for vandskader, fugtophobning og efterfølgende tørretid.
                    </p>
                  </div>

                  {/* Intro Image Showcase */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/rampe-med-maling-1-1024x923.jpg"
                        alt="Indendørs laserrens af sort maling på metalrampe i Esbjerg"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Specialiseret mobil laserrens til industri og skadeservice – 100% tør proces uden procesvand eller kemi</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Primary CTA Buttons */}
                  <div className="pt-2 flex flex-wrap items-center gap-4">
                    <a
                      href="tel:29100810"
                      className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                      <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                      <span>Send en mail</span>
                    </a>
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-2.5 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-200"
                    >
                      <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
                      <span>Få et tilbud</span>
                    </Link>
                  </div>

                  {/* Section 1 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Højeffektiv afrensning uden unødig nedetid
                    </h2>
                    <p>
                      Ved akutte indsatser og vedligeholdelse kan industriel laserrensning være relevant, når arbejdet skal udføres målrettet og med mindst mulig påvirkning af den omkringliggende drift. Laserens intensitet og frekvens tilpasses materialet, belægningen og den konkrete opgave.
                    </p>
                    <p>
                      Metoden kan blandt andet anvendes til{' '}
                      <Link
                        to="/ydelser/rust-metaloverflader"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        rust, maling, olie, fedt
                      </Link>{' '}
                      og andre fastsiddende belægninger. Laseren har ingen mekanisk kontakt med emnet, hvilket begrænser behovet for slibning og blæsning, når materialet og opgaven egner sig til laserafrensning.
                    </p>
                    <p>
                      Det kan være en fordel på byggepladser, produktionssteder og tekniske anlæg, hvor arbejdet skal koordineres med øvrige faggrupper, drift eller fremdriften i projektet.
                    </p>
                  </div>

                  {/* Section 2 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Skånsom rengøring af procesudstyr og støbeforme
                    </h2>
                    <p>
                      Inden for industriel produktion er metoden relevant til skånsom rengøring af procesudstyr, støbeforme og præcisionsværktøj. Urenheder, fedt og belægninger kan fjernes uden mekanisk slibning af overfladen.
                    </p>
                    <p>
                      På forme, profilerede dele og præcisionsværktøj er den kontrollerede behandling vigtig, fordi geometri, kanter og tolerancer skal vurderes nøje før afrensning. Laserafrensning kan desuden anvendes som klargøring før videre behandling eller som en del af løbende vedligeholdelse.
                    </p>
                    <p>
                      Den konkrete løsning afhænger altid af materialet, belægningen og kravene til den færdige overflade. Derfor vurderer vi opgaven og tilpasser laserens parametre til de aktuelle forhold.
                    </p>
                  </div>

                  {/* Mid-content Image Showcase 1 */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/metal-komponent-for-og-efter-650x650.jpg"
                        alt="Præcisionslaserrens af metalkomponent og maskindel"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Berøringsfri afrensning af maskindele – mikroskopiske tolerancer og fine strukturer bevares 100% intakte</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mid-content CTA Banner */}
                  <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-6 md:p-8 space-y-4 shadow-sm">
                    <div>
                      <span className="text-xs font-bold text-brand-green uppercase tracking-wider block mb-1">
                        Akut skadeservice &amp; rådgivning
                      </span>
                      <h3 className="text-lg md:text-xl font-bold text-zinc-950">
                        Har du brug for hurtig afrensning af procesudstyr eller sodskader?
                      </h3>
                      <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                        Vi rykker hurtigt ud med mobilt udstyr for at vurdere opgaven og udføre en eventuel{' '}
                        <Link
                          to="/priser"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                        >
                          prøverensning
                        </Link>
                        , så driftsstop og nedetid minimeres.
                      </p>
                    </div>
                    <div className="pt-2 flex flex-wrap items-center gap-3">
                      <a
                        href="tel:29100810"
                        className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-5 py-3 rounded-xl text-sm transition-all duration-300 shadow-sm"
                      >
                        <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                        <span className="text-white">Ring 29 10 08 10</span>
                      </a>
                      <a
                        href="mailto:info@ecolaser.dk"
                        className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-5 py-3 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                      >
                        <i className="fa-solid fa-envelope" aria-hidden="true" />
                        <span>Send en mail</span>
                      </a>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2.5 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-5 py-3 rounded-xl text-sm transition-all duration-300 border border-zinc-200"
                      >
                        <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
                        <span>Indhent tilbud</span>
                      </Link>
                    </div>
                  </div>

                  {/* Section 3 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Sodsanering og skimmelsvamp uden unødig fugtpåvirkning
                    </h2>
                    <p>
                      Ved skadeservice kan laserteknologien indgå i sodsanering efter brand og behandling af fastsiddende belægninger. Da laserrens er en tør proces, tilføres der ikke vand til konstruktionen under selve afrensningen. Det kan være en fordel, hvor rensevand ellers kan medføre fugtophobning eller påvirke nærliggende materialer og installationer.
                    </p>
                    <p>
                      Forstøvede restpartikler skal håndteres kontrolleret. Når opgaven kræver det, anvender vi punktudsugning tæt ved arbejdsområdet, hvor luften passerer gennem HEPA-filtre.
                    </p>
                    <p>
                      Ved mistanke om skimmelsvamp vurderes også årsagen til fugten og behovet for øvrig sanering. Laserrensning bør ikke stå alene, hvis konstruktionen fortsat er fugtig, eller hvis opgaven kræver andre specialister eller sikkerhedstiltag.
                    </p>
                  </div>

                  {/* Section 4 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Laserrens ved vedligeholdelse og byggeprojekter
                    </h2>
                    <p>
                      ECOLASER arbejder med mobilt laserudstyr, som kan bringes direkte frem til opgaven og anvendes på blandt andet produktionsudstyr, tekniske installationer og svært tilgængelige områder.
                    </p>
                    <p>
                      Den målrettede laserafrensning kan indgå i både planlagt vedligeholdelse, renoveringsprojekter, nybyggeri og akutte indsatser. Udstyret kan desuden anvendes fra blandt andet lift og stillads, når adgangsforholdene kræver det.
                    </p>
                    <p>
                      For entreprenører og bygherrer betyder det, at afrensningen kan planlægges som en del af det samlede projekt og tilpasses de konkrete materialer, adgangsforhold og krav til overfladen.
                    </p>
                  </div>

                  {/* Mid-content Image Showcase 2 */}
                  <div className="rounded-2xl overflow-hidden border border-zinc-100 bg-zinc-50 shadow-sm">
                    <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                      <img
                        src="/cdn-cgi/image/width=1000,quality=85,format=auto/_assets/malingsfjernelse-metalkonstruktion-viborg-jpg-650x650.jpg"
                        alt="Mobil laserrensning af tekniske installationer og metalkonstruktion"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/75 via-zinc-950/20 to-transparent flex items-end p-4 md:p-5">
                        <div className="text-white text-xs md:text-sm flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-brand-green shrink-0 animate-pulse" />
                          <span>Mobil laserrens på byggepladser og industrielle konstruktioner – udføres hurtigt og tørt fra bl.a. lift og stillads</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 5 */}
                  <div className="space-y-4 pt-4 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Mobil laserrens til bygge- og industriprojekter i hele Danmark
                    </h2>
                    <p>
                      Ecolaser har base i Ryomgård på Djursland og udfører mobile renseopgaver i hele Danmark med særligt fokus på projekter i Aarhus, Odense og København.
                    </p>
                    <p>
                      Vi bistår blandt andet bygherrer, entreprenører, rådgivere, virksomheder og offentlige aktører, hvor der er behov for præcis og kontrolleret afrensning af tekniske overflader, konstruktioner og udstyr.
                    </p>
                    <p>
                      Ring på{' '}
                      <a
                        href="tel:29100810"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-semibold"
                      >
                        29 10 08 10
                      </a>
                      , skriv til{' '}
                      <a
                        href="mailto:info@ecolaser.dk"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-semibold"
                      >
                        info@ecolaser.dk
                      </a>{' '}
                      eller{' '}
                      <Link
                        to="/kontakt"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        kontakt os
                      </Link>{' '}
                      for en faglig vurdering af projektet og et uforpligtende tilbud.
                    </p>
                  </div>

                  {/* Secondary CTA Buttons */}
                  <div className="pt-2 flex flex-wrap items-center gap-4">
                    <a
                      href="tel:29100810"
                      className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                      <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                      <span>Send en mail</span>
                    </a>
                  </div>
                </div>
              ) : (
                contentHtml && (
                  <div
                    className="prose prose-zinc max-w-none space-y-6 text-zinc-800 leading-relaxed text-base md:text-lg"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                )
              )}

              {/* Advantages & Process Container */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-zinc-100">
                {/* Advantages Card */}
                {advantagesList.length > 0 && (
                  <div className="bg-zinc-50 rounded-2xl p-6 md:p-8">
                    <h3 className="text-lg font-bold text-zinc-900 mb-6">
                      Fordele ved laserrens
                    </h3>
                    <ul className="space-y-4">
                      {advantagesList.map((adv, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                          <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                          <span>{adv}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Work Process Card */}
                {processList.length > 0 && (
                  <div className="bg-zinc-50 rounded-2xl p-6 md:p-8">
                    <h3 className="text-lg font-bold text-zinc-900 mb-6">
                      Arbejdsprocessen
                    </h3>
                    <div className="space-y-4">
                      {processList.map((step, i) => (
                        <div key={i} className="flex items-start gap-3 text-sm text-zinc-600 leading-relaxed">
                          <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 mt-2 shrink-0" />
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Specific Sections for Facaderens & Murværk */}
              {slug === 'facaderens-murvaerk' && (
                <div className="space-y-12 pt-6">
                  {/* Case Cards Showcase */}
                  <div className="space-y-6 pt-8 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Erfaring med facaderens og murværk
                    </h2>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Ecolaser har omfattende erfaring med kontrolleret afrensning af murværk, tegl og facader på både{' '}
                      <Link
                        to="/ydelser/fredede-bygninger"
                        className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                      >
                        fredede kulturarvsbygninger
                      </Link>
                      , ældre byejendomme og nybyggeri.
                    </p>

                    {/* Visual Case Cards Showcase */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
                      <Link
                        to="/cases/komplet-afrensning-af-historisk-stationsbygning-i-soroe-fra-1856"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/soro-station-650x650.jpg"
                            alt="Sorø Station historisk murværk"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Historisk Murværk</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Sorø Stationsbygning
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Komplet afrensning af 170 år gammelt murværk fra 1856 med bevarelse af den vitale brandhud.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>

                      <Link
                        to="/cases/skaansom-laserrens-af-bevaringsvaerdigt-funkishus-i-hellerup"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/hellerup-3_1.jpg"
                            alt="Bevaringsværdigt funkishus i Hellerup"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Gule Mursten</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Funkishus i Hellerup
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Fjernelse af saltudslag, sod og snavs i op til 9 meters højde fra stillads.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>

                      <Link
                        to="/cases/fjernelse-salpeterudtraek-mursten-kemifri-laser"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/0820-1-1024x822.png"
                            alt="Redning af mørke mursten for kalkudtræk og salpeter"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Kalk- &amp; Salpeterudtræk</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Mørke Mursten &amp; Facade
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Målrettet fjernelse af kalkudtræk og hvidt slør uden misfarvning af fugerne.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>
                    </div>
                  </div>

                  {/* Video Demonstration: Udtræk på murværk */}
                  <div className="space-y-4 pt-8 border-t border-zinc-100">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-brand-green animate-pulse" />
                      <span className="text-xs font-semibold text-brand-green uppercase tracking-wider">Video demonstration</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Se laserrens af kalk- og salpeterudtræk på murværk
                    </h2>
                    <p className="text-zinc-700 leading-relaxed text-base">
                      I videoen her ses hvordan laserlyset øjeblikkeligt fordamper det hvide overfladeudtræk på teglstenene – helt tørt og uden at beskadige stenens brandhud eller fugerne.
                    </p>
                    <div className="relative rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-950 shadow-md aspect-video max-w-3xl">
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        poster="/cdn-cgi/image/width=1200,quality=80,format=auto/_assets/moerke-mursten-1.jpg"
                        className="w-full h-full object-cover"
                      >
                        <source src="/_assets/video-udtraek-mur.mp4" type="video/mp4" />
                        Din browser understøtter ikke videoafspilning.
                      </video>
                    </div>
                  </div>

                  {/* Section: Facaderens med laser i hele Danmark */}
                  <div className="space-y-6 pt-8 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Facaderens med laser i hele Danmark
                    </h2>
                    <div className="space-y-4 text-zinc-700 leading-relaxed text-base md:text-lg">
                      <p>
                        Ecolaser har base i Ryomgård på Djursland og udfører facaderens med laser og laserafrensning af murværk i hele Danmark med særligt fokus på projekter i Aarhus, Odense og København.
                      </p>
                      <p>
                        Vi bistår blandt andet bygherrer, entreprenører, rådgivere, virksomheder og offentlige aktører med afrensning i forbindelse med nybyggeri, renovering og eksisterende ejendomme.
                      </p>
                      <p>
                        Ring på{' '}
                        <a
                          href="tel:29100810"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-semibold"
                        >
                          29 10 08 10
                        </a>
                        , skriv til{' '}
                        <a
                          href="mailto:info@ecolaser.dk"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-semibold"
                        >
                          info@ecolaser.dk
                        </a>{' '}
                        eller{' '}
                        <Link
                          to="/kontakt"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                        >
                          kontakt os
                        </Link>{' '}
                        for en faglig vurdering af projektet og et uforpligtende tilbud.
                      </p>
                    </div>

                    {/* Secondary CTA Buttons */}
                    <div className="pt-2 flex flex-wrap items-center gap-4">
                      <a
                        href="tel:29100810"
                        className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                      >
                        <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                        <span className="text-white">Ring 29 10 08 10</span>
                      </a>
                      <a
                        href="mailto:info@ecolaser.dk"
                        className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                      >
                        <i className="fa-solid fa-envelope" aria-hidden="true" />
                        <span>Send en mail</span>
                      </a>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2.5 bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-200"
                      >
                        <i className="fa-solid fa-calculator text-brand-green" aria-hidden="true" />
                        <span>Få et tilbud</span>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Specific Sections for Fredede Bygninger */}
              {slug === 'fredede-bygninger' && (
                <div className="space-y-12 pt-6">
                  {/* Section 5: Erfaring med historiske bygninger og kulturarv */}
                  <div className="space-y-6 pt-8 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Erfaring med historiske bygninger og kulturarv
                    </h2>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Ecolaser har blandt andet arbejdet med afrensning af fredede sandstensfigurer på Københavns Hovedbanegård, historisk murværk på Sorø Station og graffiti på bindingsværk ved den fredede Bondos Gård i Rudkøbing.
                    </p>

                    {/* Visual Case Cards Showcase */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
                      <Link
                        to="/cases/kobenhavns-hovedbanegard-sandstensfigurer"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/sandstensfigurer-kbh-h.jpg"
                            alt="Københavns Hovedbanegård sandstensfigurer"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">DSB Kulturarv</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Københavns Hovedbanegård
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              100 års sodskorpe fjernet fra historiske sandstensfigurer.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>

                      <Link
                        to="/cases/komplet-afrensning-af-historisk-stationsbygning-i-soroe-fra-1856"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/soro-station-650x650.jpg"
                            alt="Sorø Station historisk murværk"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Fredet Murværk</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Sorø Stationsbygning
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Komplet afrensning af 170 år gammelt murværk fra 1856.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>

                      <Link
                        to="/cases/laserrensning-fredet-bindingsvaerk-rudkobing"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/bondos-gard-650x650.jpg"
                            alt="Bondos Gård Rudkøbing bindingsværk"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Fredet Ejendom</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Bondos Gård, Rudkøbing
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Graffiti fjernet skånsomt fra 1700-tals bindingsværk og puds.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>
                    </div>
                  </div>

                  {/* Video Demonstration: Sorø Station */}
                  <div className="space-y-4 pt-8 border-t border-zinc-100">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-brand-green animate-pulse" />
                      <span className="text-xs font-semibold text-brand-green uppercase tracking-wider">Video fra projektet</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Se laserrensning af historisk murværk på Sorø Station
                    </h2>
                    <p className="text-zinc-700 leading-relaxed text-base">
                      Her udføres kontrolleret laserafrensning af 170 år gammelt murværk på Sorø Station fra 1856, hvor årtiers sod og belægninger fjernes med fuld bevarelse af den oprindelige overfladestruktur og brandhud.
                    </p>
                    <div className="relative rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-950 shadow-md aspect-video max-w-3xl">
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        poster="/cdn-cgi/image/width=1200,quality=80,format=auto/_assets/soro-station-650x650.jpg"
                        className="w-full h-full object-cover"
                      >
                        <source src="/_assets/rensning-soroe-video.mp4" type="video/mp4" />
                        Din browser understøtter ikke videoafspilning.
                      </video>
                    </div>
                  </div>

                  {/* Section 6: Laserrens af fredede bygninger i hele Danmark */}
                  <div className="space-y-6 pt-8 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Laserrens af fredede bygninger i hele Danmark
                    </h2>
                    <div className="space-y-4 text-zinc-700 leading-relaxed text-base md:text-lg">
                      <p>
                        Ecolaser har base i Ryomgård på Djursland og udfører mobil laserrensning af fredede og bevaringsværdige bygninger i hele Danmark med særligt fokus på projekter i Aarhus, Odense og København.
                      </p>
                      <p>
                        Ring på{' '}
                        <a
                          href="tel:29100810"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-semibold"
                        >
                          29 10 08 10
                        </a>
                        , skriv til{' '}
                        <a
                          href="mailto:info@ecolaser.dk"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-semibold"
                        >
                          info@ecolaser.dk
                        </a>{' '}
                        eller{' '}
                        <Link
                          to="/kontakt"
                          className="text-zinc-900 underline decoration-brand-green decoration-2 underline-offset-4 hover:text-brand-green transition-colors font-medium"
                        >
                          kontakt os
                        </Link>{' '}
                        for en faglig vurdering af bygningen og den relevante metode til restaureringsprojektet.
                      </p>
                    </div>

                    {/* Secondary CTA Buttons */}
                    <div className="pt-2 flex flex-wrap items-center gap-4">
                      <a
                        href="tel:29100810"
                        className="inline-flex items-center gap-2.5 bg-brand-green-dark hover:bg-brand-green text-white font-bold px-6 py-3.5 rounded-xl text-sm transition-all duration-300 shadow-sm"
                      >
                        <i className="fa-solid fa-phone text-white" aria-hidden="true" />
                        <span className="text-white">Ring 29 10 08 10</span>
                      </a>
                      <a
                        href="mailto:info@ecolaser.dk"
                        className="inline-flex items-center gap-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-6 py-3.5 rounded-xl text-sm transition-all duration-300 border border-zinc-800"
                      >
                        <i className="fa-solid fa-envelope" aria-hidden="true" />
                        <span>Send en mail</span>
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Specific Sections for Industri & Skadeservice */}
              {slug === 'industri-skadeservice' && (
                <div className="space-y-12 pt-6">
                  {/* Case Cards Showcase */}
                  <div className="space-y-6 pt-8 border-t border-zinc-100">
                    <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-950 tracking-tight">
                      Erfaring med sodsanering og industrielle opgaver
                    </h2>
                    <p className="text-zinc-700 leading-relaxed text-base md:text-lg">
                      Se udvalgte eksempler på opgaver, hvor Ecolasers 100% tørre laserproces har fjernet aggressiv sod og belægninger uden procesvand eller forstyrrelse af driften.
                    </p>

                    {/* Visual Case Cards Showcase */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
                      <Link
                        to="/cases/kobenhavns-hovedbanegard-sandstensfigurer"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/sandstensfigurer-kbh-h.jpg"
                            alt="Sodsanering på Københavns Hovedbanegård"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Sodsanering</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Københavns Hovedbanegård
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Over 100 års sodskorpe fjernet tørt med HEPA-filtreret punktudsugning uden procesvand.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>

                      <Link
                        to="/cases/granitfacade-rensning-kbh-trafikfilm-sokkelmaling"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/front-kbh-chr-d-9-high.webp"
                            alt="Tør afrensning ved indgangsparti Chr. D. IX Gade"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Tør proces ved indgang</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Chr. D. IX Gade, København
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              100% kemifri og tør afrensning ved befærdet indgangsparti uden vandsprøjt eller nedetid.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>

                      <Link
                        to="/cases/komplet-afrensning-af-historisk-stationsbygning-i-soroe-fra-1856"
                        className="group bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-100 hover:border-brand-green/40 hover:shadow-md transition-all duration-300 flex flex-col"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/soro-station-650x650.jpg"
                            alt="Sorø Station arbejde under togdrift og sporspærringer"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 flex-1 flex flex-col justify-between">
                          <div>
                            <span className="text-[11px] font-semibold text-brand-green uppercase tracking-wider block mb-1">Drift &amp; Sikkerhed</span>
                            <h4 className="text-sm font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                              Sorø Stationsbygning
                            </h4>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                              Udført på station i fuld drift med natarbejde og fleksibel sikkerhedskoordinering.
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-zinc-900 flex items-center gap-1.5 mt-3 group-hover:translate-x-1 transition-transform">
                            Læs case <i className="fa-solid fa-arrow-right text-[10px] text-brand-green" />
                          </span>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Image & Quick Contact Box */}
            <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
              {/* Main Service Image */}
              <div className="rounded-2xl overflow-hidden shadow-sm bg-zinc-100 aspect-[4/3]">
                <img
                  src={cardImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>

              {slug === 'facaderens-murvaerk' && (
                <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6 md:p-8 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-trowel-bricks text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-zinc-900">Specialist i murværk &amp; byggeri</h4>
                      <p className="text-xs text-zinc-500">100% kemifri &amp; tør laserafrensning</p>
                    </div>
                  </div>

                  <p className="text-sm text-zinc-600 leading-relaxed">
                    Vi samarbejder med bygherrer, entreprenører og rådgivere i hele landet om skånsom udbedring af salpeterudtræk, kalkslør og facaderens på nybyggeri og ældre ejendomme.
                  </p>

                  <div className="pt-2 border-t border-zinc-200/80 space-y-2.5">
                    <a
                      href="tel:29100810"
                      className="w-full bg-brand-green-dark hover:bg-brand-green text-white font-bold px-4 py-3 rounded-xl text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" /> <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="w-full bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-4 py-3 rounded-xl text-xs uppercase tracking-wider border border-zinc-200 transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" /> Send mail: info@ecolaser.dk
                    </a>
                  </div>
                </div>
              )}

              {slug === 'fredede-bygninger' && (
                <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6 md:p-8 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-shield-halved text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-zinc-900">Specialist i kulturarv</h4>
                      <p className="text-xs text-zinc-500">100% kemifri &amp; non-abrasiv laserrens</p>
                    </div>
                  </div>

                  <p className="text-sm text-zinc-600 leading-relaxed">
                    Vi samarbejder tæt med bygherrer, rådgivende arkitekter og fredningsmyndigheder i hele Danmark for at sikre den mest skånsomme afrensning af historiske materialer.
                  </p>

                  <div className="pt-2 border-t border-zinc-200/80 space-y-2.5">
                    <a
                      href="tel:29100810"
                      className="w-full bg-brand-green-dark hover:bg-brand-green text-white font-bold px-4 py-3 rounded-xl text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" /> <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="w-full bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-4 py-3 rounded-xl text-xs uppercase tracking-wider border border-zinc-200 transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" /> Send mail: info@ecolaser.dk
                    </a>
                  </div>
                </div>
              )}

              {slug === 'industri-skadeservice' && (
                <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6 md:p-8 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-industry text-lg" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-zinc-900">Industri &amp; Skadeservice</h4>
                      <p className="text-xs text-zinc-500">100% vandfri &amp; mobil laserrensning</p>
                    </div>
                  </div>

                  <p className="text-sm text-zinc-600 leading-relaxed">
                    Vi hjælper industrivirksomheder, entreprenører og skadeservice med præcisionsrensning af støbeforme, procesudstyr og tør sodsanering uden unødig nedetid.
                  </p>

                  <div className="pt-2 border-t border-zinc-200/80 space-y-2.5">
                    <a
                      href="tel:29100810"
                      className="w-full bg-brand-green-dark hover:bg-brand-green text-white font-bold px-4 py-3 rounded-xl text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 shadow-sm"
                    >
                      <i className="fa-solid fa-phone text-white" aria-hidden="true" /> <span className="text-white">Ring 29 10 08 10</span>
                    </a>
                    <a
                      href="mailto:info@ecolaser.dk"
                      className="w-full bg-white hover:bg-zinc-100 text-zinc-900 font-semibold px-4 py-3 rounded-xl text-xs uppercase tracking-wider border border-zinc-200 transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <i className="fa-solid fa-envelope" aria-hidden="true" /> Send mail: info@ecolaser.dk
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-900 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-5">
          <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
            Skal vi hjælpe dig med {title.toLowerCase()}?
          </h2>
          <p className="text-zinc-300 text-sm md:text-base max-w-xl leading-relaxed">
            Vi dækker hele Danmark og kommer gerne ud og laver en prøverensning på din overflade.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 w-full sm:w-auto">
            <a
              href={`tel:${phone}`}
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Ring direkte: {phone}
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-transparent hover:bg-transparent hover:text-white/80 hover:border-white/80 text-white font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider border border-white transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Udfyld formular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
